import React from 'react';
import dayjs from 'dayjs';

import WorkflowStatusTag from 'shared/workflowStatusTag/WorkflowStatusTag';
import ListingStatusBadge from 'shared/listingStatusBadge/ListingStatusBadge';
import TaskOverViewIcon from 'components/atoms/TaskOverViewIcon/TaskOverViewIcon';
import { DATE_END_TIME, DATE_START_TIME, DATE_ISO_FORMAT } from 'utils/date';
import { ListingStatusId, ListingWorkflowStatusId } from 'utils/const';

const { Created, Live, Sold, Hidden, Removed } = ListingStatusId;

/* eslint-disable react/display-name */
export const listColumns = [
  {
    title: 'Litter Id',
    dataIndex: 'litterId',
    key: 'litter_id',
    sortDirections: ['descend', 'ascend'],
    sorter: (a, b) => false,
    width: 150,
  },
  {
    title: 'Status',
    dataIndex: 'workflowStatus',
    key: 'workflow_status',
    render: (text, record) => (
      <WorkflowStatusTag
        workflowStatusId={record.workflowStatusId}
        useAcronym
      />
    ),
  },
  { title: 'Puppy Id', dataIndex: 'puppyId', key: 'puppy_id' },
  {
    title: 'Name',
    dataIndex: 'breederPuppyName',
    key: 'breeder_puppy_name',
    ellipsis: true,
    width: 110,
  },
  {
    title: 'Breed',
    dataIndex: 'breedAdLabel',
    key: 'breed_ad_label',
    ellipsis: true,
    width: 180,
  },
  {
    title: 'Breeder',
    dataIndex: 'breeder',
    key: 'breeder',
    ellipsis: true,
    width: 180,
  },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    key: 'created_at',
    sortDirections: ['descend', 'ascend'],
    sorter: (a, b) => false,
  },
  {
    title: 'Updated',
    dataIndex: 'logicalUpdatedAt',
    key: 'logical_updated_at',
    sortDirections: ['descend', 'ascend'],
    sorter: (a, b) => false,
  },
  {
    title: 'Listing Status',
    dataIndex: 'listingStatus',
    key: 'listingStatus',
    render: (text, record) => (
      <ListingStatusBadge listingStatusId={record.listingStatusId} />
    ),
  },
  {
    title: 'Task Overview',
    dataIndex: 'taskOverView',
    key: 'taskOverView',
    render: (text, record) => (
      <div className="pps-task-overview-contaner">
        <TaskOverViewIcon icon={'puppy'} status={'complete'} />
        <TaskOverViewIcon icon={'sire'} status={'incomplete'} />
        <TaskOverViewIcon icon={'litter'} status={'complete'} />
        <a href={`/listing/${record.key}`} rel="noopener noreferrer">
          GO
        </a>
      </div>
    ),
  },
];

export const getCurrentSearchArray = (searchArray, dataIndex, selectedKeys) => {
  let currentSearchArray = searchArray;
  const foundIndex = searchArray.findIndex(
    (sItem) => sItem.dataIndex === dataIndex
  );
  if (foundIndex > -1) {
    currentSearchArray.splice(foundIndex, 1);
  }
  const newSearchObject = [
    {
      dataIndex,
      dataValue: selectedKeys[0],
    },
  ];
  return [...currentSearchArray, ...newSearchObject];
};

export const thereIsAnActiveSearch = (searchArray, key) => {
  return searchArray.find((sItem) => sItem.dataIndex === key);
};

export const prepareSearch = (searchArray) => {
  return searchArray.map(
    (sItem) => `(term field=${sItem.dataIndex} '${sItem.dataValue}')`
  );
};

export const getStartingPointForQuery = (page, pageSize) => {
  if (page === 2) {
    return (page * pageSize) / 2;
  }
  return page * pageSize - pageSize;
};

export const availableQuery = (search) => {
  const query = `(or listing_status_id:${Created} listing_status_id:${Live} listing_status_id:${Hidden})`;
  if (search) {
    return `(and ${prepareSearch(search)} ${query})`;
  }
  return query;
};

export const unavailableQuery = (search) => {
  let rangeDateFrom = getTimeRangeFromXMonthsAgo(3);
  const query = `(and logical_updated_at:[${rangeDateFrom}] (or listing_status_id:${Removed} listing_status_id:${Sold}))`;
  if (search) {
    return `(and ${prepareSearch(search)} ${query})`;
  }
  return query;
};
export const pendingBreederQuery = (search) => {
  const { Postponed } = ListingWorkflowStatusId;
  const query = `(and workflow_status_id:${Postponed} (not (or listing_status_id:${Removed} listing_status_id:${Sold})))`;
  if (search) {
    return `(and ${prepareSearch(search)} ${query})`;
  }
  return query;
};

export const generateQueryByHeaderSearch = (parameters, search) => {
  const splittedParameters = parameters.split('&');
  const headerQuery = `(and (term ${splittedParameters[0]} ${splittedParameters[1]}))`;
  return search !== '' ? `( and ${search} ${headerQuery} )` : headerQuery;
};

export const getLockedColumn = (parameters) => {
  const spplitedParameters = parameters.split('&');
  return spplitedParameters[0].replace('field=', '');
};

const currentDateAndTime = () => {
  const currentDate = dayjs().format(DATE_ISO_FORMAT);
  const currentTime = DATE_END_TIME;
  const currentDateAndTime = `${currentDate}${currentTime}`;
  return currentDateAndTime;
};

const dateAndTimeXMonthsInPast = (monthsInThePast) => {
  const xMonthsInThePast = dayjs()
    .subtract(monthsInThePast, 'months')
    .format(DATE_ISO_FORMAT);
  const startTime = DATE_START_TIME;
  const dateAndTimeInPast = `${xMonthsInThePast}${startTime}`;
  return dateAndTimeInPast;
};

export const getTimeRangeFromXMonthsAgo = (monthsInThePast) => {
  const dateAndTimeInPast = dateAndTimeXMonthsInPast(monthsInThePast);
  return `'${dateAndTimeInPast}', '${currentDateAndTime()}'`;
};
